








































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import i18n from '@/services/i18n';
import * as common from '../common';
import { ComponentBase } from '../components';
import type { Dict, Report2, Utils } from '../types';
import FzpRejectReasonCell from './FzpRejectReasonCell.vue';
import FzpReportRow from './FzpReportRow.vue';
import * as fzp from './fzp';
import type { Fzp } from './types';


/*
Итоги первой генерации силами ИИ:

  Что реализовано:

  Шапка таблицы — первая колонка "Наименование" занимает все строки шапки (rowspan), остальные колонки строятся по
  rootColumnGroups через дерево колонок (логика скопирована из ReportPage.vue).

  Иерархия строк — три уровня, каждый в своём <template v-if> для корректного Vue-рендеринга:
  - Функциональная группа (синий вариант) — всегда видима, кнопка +/− скрывает/показывает весь блок подгрупп
  - Функциональная подгруппа (отступ 24px) — кнопка +/− скрывает/показывает строки АБП
  - АБП (отступ 48px) — листовые строки без кнопки

  Значения — для АБП-строк берётся getCellValue из reportRow.cells, для группы и подгруппы — sumCellValues суммирует все дочерние строки.

  Типы — добавлено childrenExpanded: boolean в TableRowParent с документацией; все существующие JSDoc-комментарии сохранены.
 */


// region Типы
/**
 * Свойства текущего компонента в виде одного объекта
 */
interface LocalProps {
    /**
     * Содержимое листа отчета
     */
    data: Fzp.ReactiveReportPart;

    /**
     * Отображаемая часть отчета (отображаются только те группы колонок и колонки, в которых есть такой "тип отчета")
     */
    part: string;

    /**
     * Контроллер для управления содержимым отчета: определение редактируемых колонок, пересчет содержимого строки и т.п.
     */
    controller: Fzp.Controller;
}


/**
 * Тип строки в таблице.
 *
 * Возможные значения:
 *
 * * 'FUNC_GROUP' - функциональная группа;
 * * 'FUNC_SUBGROUP' - функциональная подгруппа;
 * * 'ABP' - АБП.
 */
type TableRowType = 'FUNC_GROUP' | 'FUNC_SUBGROUP' | 'ABP';

/**
 * Строка таблицы интерфейса (базовый интерфейс)
 */
interface TableRowBase {
    /**
     * Тип строки
     */
    type: TableRowType;

    /**
     * Строки из содержимого отчета
     */
    reportRows: Array<Fzp.ReactiveReportRow>;

    /**
     * Ключ строки
     */
    rowKey: string;
}

/**
 * Строка таблицы интерфейса (базовый интерфейс родительской строки)
 */
interface TableRowParent extends TableRowBase {
    /**
     * Дочерние строки таблицы
     */
    children: Array<TableRowChild>;

    /**
     * Признак "дочерние строки отображаются"
     */
    childrenExpanded: boolean;
}

/**
 * Строка таблицы интерфейса (базовый интерфейс дочерней строки)
 */
interface TableRowChild extends TableRowBase {
    /**
     * Признак "строка таблицы отображается"
     */
    visible: boolean;
}

/**
 * Строка таблицы для функциональной группы (верхний уровень иерархии)
 */
interface TableRowFunctionalGroup extends TableRowParent {
    /**
     * Тип строки
     */
    type: 'FUNC_GROUP';

    /**
     * Данные о функциональной группе
     */
    functionalGroup: Dict.EbkFunc;

    /**
     * Дочерние строки таблицы
     */
    children: Array<TableRowFunctionalSubgroup>;
}

/**
 * Строка таблицы для функциональной подгруппы (средний уровень иерархии)
 */
interface TableRowFunctionalSubgroup extends TableRowParent, TableRowChild {
    /**
     * Тип строки
     */
    type: 'FUNC_SUBGROUP';

    /**
     * Данные о функциональной группе
     */
    functionalGroup: Dict.EbkFunc;

    /**
     * Данные о функциональной подгруппе
     */
    functionalSubgroup: Dict.EbkFunc;

    /**
     * Дочерние строки таблицы
     */
    children: Array<TableRowAbp>;
}

/**
 * Строка таблицы для АБП (нижний уровень иерархии)
 */
interface TableRowAbp extends TableRowChild {
    /**
     * Тип строки
     */
    type: 'ABP';

    /**
     * Данные о функциональной группе
     */
    functionalGroup: Dict.EbkFunc;

    /**
     * Данные о функциональной подгруппе
     */
    functionalSubgroup: Dict.EbkFunc;

    /**
     * Данные об АБП
     */
    abp: Dict.EbkFunc;

    /**
     * Строка из содержимого отчета
     */
    reportRow: Fzp.ReactiveReportRow;
}
// endregion


// region Утилиты - дерево колонок
interface ColumnTreeItem {
    parent: ColumnTreeItem | null;
    item: Report2.Content.ColumnItem;
    hidden: boolean;
    level: number;
    colSpan: number;
    rowSpan: number;
    children: Array<ColumnTreeItem>;
}

interface ColumnTreeData {
    rows: Array<Array<ColumnTreeItem>>;
    columns: Array<Report2.Content.ColumnItem.Column>;
}

const convertColumnItemsToTreeItems = (source: Array<Report2.Content.ColumnItem>, reportType: string, parent?: ColumnTreeItem): Array<ColumnTreeItem> => {
    const preparedParent = parent ?? null;
    const level = (parent?.level ?? -1) + 1;

    const result: Array<ColumnTreeItem> = [];
    for (const sourceItem of source) {
        const reportTypes = sourceItem.reportTypes;
        if ((reportTypes !== undefined) && (reportTypes !== null) && (!reportTypes.includes(reportType))) {
            continue;
        }

        const resultItem: ColumnTreeItem = {
            parent: preparedParent,
            item: sourceItem,
            hidden: false,
            level,
            colSpan: 1,
            rowSpan: 1,
            children: [],
        };

        if (sourceItem.type === 'GROUP') {
            const sourceChildren = sourceItem.items;
            if (sourceChildren.length > 0) {
                resultItem.children = convertColumnItemsToTreeItems(sourceChildren, reportType, resultItem);

                resultItem.colSpan = 0;
                for (const childItem of resultItem.children) {
                    resultItem.colSpan += childItem.colSpan;
                }
                if (resultItem.colSpan < 1) {
                    resultItem.colSpan = 1;
                }
            }
        }

        result.push(resultItem);
    }

    return result;
};

const scanColumnItemsForMaxLevel = (target: { level: number }, items: Array<ColumnTreeItem>): number => {
    if (items.length > 0) {
        if (target.level < items[0].level) {
            target.level = items[0].level;
        }

        for (const item of items) {
            if (item.children.length > 0) {
                scanColumnItemsForMaxLevel(target, item.children);
            }
        }
    }
    return target.level;
};

const fixLevelsOfColumnItems = (items: Array<ColumnTreeItem>) => {
    for (const item of items) {
        const parent = item.parent;
        if (parent === null) {
            item.level = 0;
        } else {
            if (parent.hidden) {
                item.level = parent.level;
            } else {
                item.level = parent.level + 1;
            }
        }

        const sourceItem = item.item;
        if (sourceItem.type === 'GROUP') {
            if (sourceItem.hiddableCell && (item.children.length < 2)) {
                item.hidden = true;
            }
            fixLevelsOfColumnItems(item.children);
        }
    }
};

const fixRowSpansOfColumnItems = (maxLevel: number, items: Array<ColumnTreeItem>) => {
    for (const treeItem of items) {
        const columnItem = treeItem.item;
        if (columnItem.type === 'COLUMN') {
            treeItem.rowSpan = (maxLevel - treeItem.level) + 1;
        } else {
            fixRowSpansOfColumnItems(maxLevel, treeItem.children);
        }
    }
};

const defineColumnTreeRoots = (source: Array<Report2.Content.ColumnItem>, reportType: string): Array<ColumnTreeItem> => {
    const result: Array<ColumnTreeItem> = convertColumnItemsToTreeItems(source, reportType);

    fixLevelsOfColumnItems(result);

    const maxLevel = scanColumnItemsForMaxLevel({ level: 0 }, result);
    fixRowSpansOfColumnItems(maxLevel, result);

    return result;
};

const placeColumnItemsIntoTreeData = (target: ColumnTreeData, treeItems: Array<ColumnTreeItem>) => {
    for (const treeItem of treeItems) {
        const requiredRowCount = treeItem.level + 1;
        while (target.rows.length < requiredRowCount) {
            target.rows.push([]);
        }

        const row = target.rows[treeItem.level];
        row.push(treeItem);

        const item = treeItem.item;
        if (item.type === 'COLUMN') {
            target.columns.push(item);
        } else {
            placeColumnItemsIntoTreeData(target, treeItem.children);
        }
    }
};

const defineColumnTreeData = (source: Array<Report2.Content.ColumnItem>, reportType: string): ColumnTreeData => {
    const roots = defineColumnTreeRoots(source, reportType);

    const result: ColumnTreeData = { rows: [], columns: [] };
    placeColumnItemsIntoTreeData(result, roots);

    return result;
};
// endregion


const stringSorter: ((a: string, b: string) => number) = (a, b) => {
    // Если бы сравнивались числа, то применялось бы выражение "a - b"
    if (a > b) {
        return 1;
    } else if (a < b) {
        return -1;
    } else {
        return 0;
    }
};


/**
 * Утилиты для dict_ebk_func
 */
const dictEbkFuncUtils = {
    /**
     * Конвертация "number" в строку с 3 цифрами, если "number" является числом
     *
     * @param number конвертируемое число
     */
    numberTo3DigitString(number: number | null): string {
        if (number === null) {
            return 'null';
        } else if (!Number.isSafeInteger(number)) {
            return 'NaN';
        } else if (number === 0) {
            return '000';
        } else if (number < 0) {
            const positiveNumber = -number;
            return `-${positiveNumber.toString().padStart(3, '0')}`;
        } else {
            return number.toString().padStart(3, '0');
        }
    },


    /**
     * Получение строки кода ФГ
     *
     * @param gr числовой код ФГ
     */
    grCodeString(gr: number | null): string {
        return this.numberTo3DigitString(gr);
    },

    /**
     * Получение строки кода ФГП в формате "ФГ/ФПГ"
     *
     * @param gr код ФГ
     * @param pgr код ФПГ
     */
    pgrCodeString(gr: number | null, pgr: number | null): string {
        return `${this.grCodeString(gr)}/${this.numberTo3DigitString(pgr)}`;
    },

    /**
     * Получение строки кода АБП в формате "ФГ/ФПГ/АБП"
     *
     * @param gr код ФГ
     * @param pgr код ФПГ
     * @param abp код АБП
     */
    abpCodeString(gr: number | null, pgr: number | null, abp: number | null): string {
        return `${this.pgrCodeString(gr, pgr)}/${this.numberTo3DigitString(abp)}`;
    },
};


const isRejectReasonChanged = (reportPart: Fzp.ReactiveReportPart, allFgRows: Array<TableRowFunctionalGroup>, row: TableRowFunctionalGroup | TableRowFunctionalSubgroup | TableRowAbp | null): boolean => {
    const rowKey = row?.rowKey ?? fzp.getRowKey(null, null, null);

    if (reportPart.isRejectReasonChanged(rowKey)) {
        return true;
    }

    let children: Array<TableRowFunctionalGroup | TableRowFunctionalSubgroup | TableRowAbp> | null;
    if (row === null) {
        children = allFgRows;
    } else if (row.type === 'FUNC_GROUP') {
        children = row.children;
    } else if (row.type === 'FUNC_SUBGROUP') {
        children = row.children;
    } else {
        children = null;
    }

    if (children !== null) {
        for (const child of children) {
            if (isRejectReasonChanged(reportPart, allFgRows, child)) {
                return true;
            }
        }
    }

    return false;
};


const translates = {
    totalRow: {
        get title(): string {
            return i18n.choose(
                'Всего',
                'Барлығы',
                'Total',
            );
        },
    },
};


@Component({
    components: {
        FzpRejectReasonCell,
        FzpReportRow,
    },
})
export default class FzpReportPart extends ComponentBase {
    constructor() {
        super('');
    }


    // region Модель, свойства
    /**
     * [Свойство компонента] Отображаемый лист отчета
     */
    @Prop({ required: true })
    public readonly sheet!: string;

    /**
     * [Свойство компонента] Содержимое листа отчета
     */
    @Prop({ required: true })
    public readonly data!: Fzp.ReactiveReportPart;

    /**
     * [Свойство компонента] Отображаемая часть отчета (отображаются только те группы колонок и колонки,
     * в которых есть такой "тип отчета")
     */
    @Prop({ required: true })
    public readonly part!: string;

    /**
     * [Свойство компонента] Контроллер для управления содержимым отчета: определение редактируемых колонок,
     * пересчет содержимого строки и т.п.
     */
    @Prop({ required: true })
    public readonly controller!: Fzp.Controller;

    /**
     * Свойства текущего компонента в виде одного объекта
     */
    public get localProps(): LocalProps {
        return {
            data: this.data,
            part: this.part,
            controller: this.controller,
        };
    }

    @Watch('localProps', { immediate: true })
    private onLocalPropsChange(localProps: LocalProps) {
        this.tableRows = this.buildTableRows(localProps.data);
    }
    // endregion


    // region Утилиты
    private translates = translates;

    private getSheetRowKey(localRowKey: string): string {
        return `${this.sheet}**${localRowKey}`;
    }

    private get numberFormatter(): Utils.NumberInput.Formatter {
        return common.createFormatter(i18n.locale, 0, 3);
    }
    // endregion


    // region Строки таблицы
    public tableRows: Array<TableRowFunctionalGroup> = [];

    private buildTableRows(reportPart: Fzp.ReactiveReportPart): Array<TableRowFunctionalGroup> {
        const fgMap = new Map<string, Dict.EbkFunc>();
        for (const fg of reportPart.functionalGroupList) {
            if (fg.gr !== null) {
                const code = dictEbkFuncUtils.grCodeString(fg.gr);
                fgMap.set(code, fg);
            }
        }

        const fsgMap = new Map<string, Dict.EbkFunc>();
        for (const fsg of reportPart.functionalSubgroupList) {
            if (fsg.pgr !== null) {
                const code = dictEbkFuncUtils.pgrCodeString(fsg.gr, fsg.pgr);
                fsgMap.set(code, fsg);
            }
        }

        const abpDictMap = new Map<string, Dict.EbkFunc>();
        for (const abp of reportPart.abpList) {
            if (abp.abp !== null) {
                const code = dictEbkFuncUtils.abpCodeString(abp.gr, abp.pgr, abp.abp);
                abpDictMap.set(code, abp);
            }
        }

        // Группировка строк: функциональная группа -> подгруппа -> АБП
        const grouped = new Map<string, Map<string, Map<string, Fzp.ReactiveReportRow>>>();
        for (const row of reportPart.rows) {
            const grCode = dictEbkFuncUtils.grCodeString(row.functionalGroupCode);
            const pgrCode = dictEbkFuncUtils.pgrCodeString(row.functionalGroupCode, row.functionalSubgroupCode);
            const abpCode = dictEbkFuncUtils.abpCodeString(row.functionalGroupCode, row.functionalSubgroupCode, row.abpCode);

            let byFsg = grouped.get(grCode);
            if (!byFsg) {
                byFsg = new Map();
                grouped.set(grCode, byFsg);
            }

            let byAbp = byFsg.get(pgrCode);
            if (!byAbp) {
                byAbp = new Map();
                byFsg.set(pgrCode, byAbp);
            }

            byAbp.set(abpCode, row as Fzp.ReactiveReportRow);
        }

        const result: Array<TableRowFunctionalGroup> = [];

        const sortedFgCodes = [...grouped.keys()].sort(stringSorter);
        for (const fgCode of sortedFgCodes) {
            const fg = fgMap.get(fgCode);
            if (!fg) {
                continue;
            }

            const byFsg = grouped.get(fgCode)!;
            const fgReportRows: Array<Fzp.ReactiveReportRow> = [];
            const fsgRows: Array<TableRowFunctionalSubgroup> = [];

            const sortedFsgCodes = [...byFsg.keys()].sort(stringSorter);
            for (const fsgCode of sortedFsgCodes) {
                const fsg = fsgMap.get(fsgCode);
                if (!fsg) {
                    continue;
                }

                const byAbp = byFsg.get(fsgCode)!;
                const fsgReportRows: Array<Fzp.ReactiveReportRow> = [];
                const abpRows: Array<TableRowAbp> = [];

                const sortedAbpCodes = [...byAbp.keys()].sort(stringSorter);
                for (const abpCode of sortedAbpCodes) {
                    const abp = abpDictMap.get(abpCode);
                    if (!abp) {
                        continue;
                    }

                    const row = byAbp.get(abpCode)!;
                    fsgReportRows.push(row);
                    fgReportRows.push(row);

                    abpRows.push({
                        type: 'ABP',
                        functionalGroup: fg,
                        functionalSubgroup: fsg,
                        abp,
                        reportRow: row,
                        reportRows: [row],
                        rowKey: fzp.getRowKey(fg.gr!, fsg.pgr, abp.abp),
                        visible: true,
                    });
                }

                const fsgRowKey = fzp.getRowKey(fg.gr!, fsg.pgr, null);
                fsgRows.push({
                    type: 'FUNC_SUBGROUP',
                    functionalGroup: fg,
                    functionalSubgroup: fsg,
                    reportRows: fsgReportRows,
                    rowKey: fsgRowKey,
                    children: abpRows,
                    visible: true,
                    childrenExpanded: fzp.localState.isRowExpanded(this.getSheetRowKey(fsgRowKey)),
                });
            }

            const fgRowKey = fzp.getRowKey(fg.gr!, null, null);
            result.push({
                type: 'FUNC_GROUP',
                functionalGroup: fg,
                reportRows: fgReportRows,
                rowKey: fgRowKey,
                children: fsgRows,
                childrenExpanded: fzp.localState.isRowExpanded(this.getSheetRowKey(fgRowKey)),
            });
        }

        return result;
    }

    /**
     * Строки из содержимого отчета всех строк верхнего уровня (функциональных групп), используемые для расчета
     * значений строки "Всего"
     */
    public get totalReportRows(): Array<Fzp.ReactiveReportRow> {
        const result: Array<Fzp.ReactiveReportRow> = [];
        for (const fgRow of this.tableRows) {
            result.push(...fgRow.reportRows);
        }
        return result;
    }

    /**
     * Ключ строки "Всего" (не привязан к конкретной ФГ/ФПГ/АБП)
     */
    public get totalRowKey(): string {
        return fzp.getRowKey(null, null, null);
    }
    // endregion


    // region Колонки
    public get columnTreeData(): ColumnTreeData | null {
        const { data, part } = this.localProps;

        if (data.rootColumnGroups.length === 0) {
            return null;
        }
        return defineColumnTreeData(data.rootColumnGroups, part);
    }

    public get columns(): Array<Report2.Content.ColumnItem.Column> {
        return this.columnTreeData?.columns ?? [];
    }
    // endregion


    // region Форматирование и переводы
    public get nameColumnTitle(): string {
        return i18n.choose('Наименование', 'Атауы', 'Name');
    }

    public getColumnItemName(columnItem: Report2.Content.ColumnItem): string {
        return columnItem.name.valueMap[i18n.locale] ?? columnItem.name.defaultValue;
    }

    private getEbkFuncName(item: Dict.EbkFunc): string {
        return i18n.choose(item.nameRu ?? '', item.nameKk ?? item.nameRu ?? '');
    }

    public formatFunctionalGroup(item: Dict.EbkFunc): string {
        const code = item.gr !== null ? String(item.gr).padStart(2, '0') : null;
        const name = this.getEbkFuncName(item);
        return code ? `${code} - ${name}` : name;
    }

    public formatFunctionalSubgroup(item: Dict.EbkFunc): string {
        const code = item.pgr !== null ? String(item.pgr).padStart(2, '0') : null;
        const name = this.getEbkFuncName(item);
        return code ? `${code} - ${name}` : name;
    }

    public formatAbp(item: Dict.EbkFunc): string {
        const code = item.abp !== null ? String(item.abp).padStart(3, '0') : null;
        const name = this.getEbkFuncName(item);
        return code ? `${code} - ${name}` : name;
    }

    public getTableRowVariant(row: TableRowFunctionalGroup | TableRowFunctionalSubgroup | TableRowAbp | null): string {
        let reportRows: Array<Fzp.ReactiveReportRow>;
        if (row === null) {
            reportRows = this.totalReportRows;
        } else {
            reportRows = row.reportRows;
        }

        let changed: boolean;

        // Проверка изменений в строках отчета
        changed = (reportRows.find((reportRow) => reportRow.isChanged) !== undefined);

        // Проверка изменений в обосновании
        if ((!changed) && isRejectReasonChanged(this.data, this.tableRows, row)) {
            changed = true;
        }

        if (changed) {
            return 'success';
        } else {
            return '';
        }
    }
    // endregion


    // region Значения ячеек
    public getCellValue(row: Fzp.ReportRow, columnKey: string): number {
        const cell = row.cells.find((c) => c.columnKey === columnKey);
        return cell?.value ?? 0;
    }

    public sumCellValues(rows: Array<Fzp.ReportRow>, columnKey: string): number {
        let sum = 0;
        for (const row of rows) {
            for (const cell of row.cells) {
                if (cell.columnKey === columnKey) {
                    sum += cell.value;
                    break;
                }
            }
        }
        return sum;
    }

    public sumCellValuesAndFormat(rows: Array<Fzp.ReportRow>, columnKey: string): string {
        const value = this.sumCellValues(rows, columnKey);
        const result = this.numberFormatter(value);
        return result ?? '';
    }
    // endregion


    // region Сворачивание/разворачивание веток ФГ, ФПГ
    public toggleFunctionalGroup(row: TableRowFunctionalGroup) {
        row.childrenExpanded = !row.childrenExpanded;
        fzp.localState.setRowExpanded(this.getSheetRowKey(row.rowKey), row.childrenExpanded);
    }

    public toggleFunctionalSubgroup(row: TableRowFunctionalSubgroup) {
        row.childrenExpanded = !row.childrenExpanded;
        fzp.localState.setRowExpanded(this.getSheetRowKey(row.rowKey), row.childrenExpanded);
    }
    // endregion
}
